
import { defineComponent, reactive, toRefs, onMounted } from 'vue'
import AdaptiveWindowLayout from '@/layouts/AdaptiveWindowLayout.vue'
import { IconType } from '@/enums/IconType.ts'
import DateRangePicker from '@/components/composite/DateRangePicker.vue'
import Checkbox, { CheckboxSizeType, CheckboxType } from '@/components/base/controls/Checkbox.vue'
import Table, { baseTableHeader } from '@/components/base/Table.vue'
import moment from 'moment'
import workStatusApi from '@/apis/workStatus'
import { AllCompletedWorkStatusParams, AllCompletedWorkStatusResponse } from '@/types/workStatusType'
import store, { MutationName } from '@/store'
import router from '@/router'
import Role from '@/enums/Role'
import { handleErrorToast } from '@/helpers/errorHandler'
import { WorkStateQueryType } from '@/enums/queryString'

export interface ChangeDateDay {
  startDate: string
  endDate: string
}

export enum ChangeDateType {
  CHECKBOX = 'checkbox',
  DATE_RANGE = 'date_range',
}

export interface Member extends AllCompletedWorkStatusResponse {
  userName: string
  battery: number
  warehouseRepair: number
  fieldRepair: number
  pickup: number
  dropoff: number
}

export default defineComponent({
  emits: ['update:modelValue'],
  name: 'MembersWork',
  components: {
    AdaptiveWindowLayout,
    DateRangePicker,
    Checkbox,
    Table,
  },
  setup: function () {
    const nowDate = moment().format('YYYY-MM-DD')
    const state = reactive({
      isLoaded: false,
      startDate: nowDate,
      endDate: nowDate,
      checkedDateRangeId: nowDate,
      dateRange: nowDate,
      tableHeader: [
        Object.assign({}, baseTableHeader, {
          id: 'userName',
          name: '아이디',
          width: '80px',
          flexGrow: 1,
          enableDescendingSort: true,
          dataTextAlign: 'left',
          dataFontSize: '14px',
          dataFontWeight: 700,
        }),
        Object.assign({}, baseTableHeader, {
          id: 'battery',
          name: '배터리',
          width: '60px',
          dataFontSize: '14px',
          enableDescendingSort: true,
        }),
        Object.assign({}, baseTableHeader, {
          id: 'warehouseRepair',
          name: '입고수리',
          width: '60px',
          dataFontSize: '14px',
          enableDescendingSort: true,
        }),
        Object.assign({}, baseTableHeader, {
          id: 'fieldRepair',
          name: '현장수리',
          width: '60px',
          dataFontSize: '14px',
          enableDescendingSort: true,
        }),
        Object.assign({}, baseTableHeader, { id: 'pickup', name: '상차', width: '60px', enableDescendingSort: true }),
        Object.assign({}, baseTableHeader, {
          id: 'dropoff',
          name: '하차',
          width: '60px',
          dataFontSize: '14px',
          enableDescendingSort: true,
        }),
      ],
      tableContent: [
        {
          userName: '',
          battery: 0,
          warehouseRepair: 0,
          fieldRepair: 0,
          pickup: 0,
          dropoff: 0,
        },
      ] as Array<Member>,
      dateFilterItems: [
        {
          id: moment().format('YYYY-MM-DD'),
          value: '당일',
        },
        {
          id: moment().subtract(1, 'days').format('YYYY-MM-DD'),
          value: '전일',
        },
        {
          id: moment().subtract(1, 'month').format('YYYY-MM-DD'),
          value: '한달',
        },
      ],
    })

    const getCompleteWorkData = async (
      params: AllCompletedWorkStatusParams = {
        user_id: store.state.me.id,
        start_day: moment(state.startDate).format('YYYYMMDD'),
        end_day: moment(state.endDate).format('YYYYMMDD'),
        personal: false,
      }
    ) => {
      state.isLoaded = false
      try {
        const { data: workStatusData } = await workStatusApi.getAllCompletedWorkStatus(
          store.state.currentRegion.id,
          params
        )
        state.tableContent = workStatusData.map((data, dataIndex) => {
          return {
            ...data,
            userName: workStatusData[dataIndex].user_name,
            battery: workStatusData[dataIndex].complete_group_count.battery,
            warehouseRepair: workStatusData[dataIndex].complete_group_count.warehouse_repair,
            fieldRepair: workStatusData[dataIndex].complete_group_count.field_repair,
            pickup: workStatusData[dataIndex].complete_group_count.pickup,
            dropoff: workStatusData[dataIndex].complete_group_count.dropoff,
          }
        })
      } catch (e: any) {
        handleErrorToast(e)
      } finally {
        state.isLoaded = true
      }
    }

    const changeDate = (day: ChangeDateDay, type: ChangeDateType) => {
      switch (type) {
        case ChangeDateType.CHECKBOX: {
          state.startDate = day.startDate
          state.endDate = day.endDate
          break
        }
        case ChangeDateType.DATE_RANGE: {
          const today = state.startDate === nowDate
          const yesterday =
            state.startDate === moment().subtract(1, 'day').format('YYYY-MM-DD') && state.endDate === nowDate
          const lastMonth =
            state.startDate === moment().subtract(1, 'month').format('YYYY-MM-DD') && state.endDate === nowDate

          state.checkedDateRangeId = today || yesterday || lastMonth ? state.startDate : ''
          break
        }
      }
      getCompleteWorkData()
    }

    const goMemberWork = (name: string) => {
      const member = state.tableContent.find((content) => content.userName === name) as Member
      const role = store.state[MutationName.DEFAULT_AREA].role
      if (!role.includes(Role.ADMIN)) {
        return
      }
      router.push(
        `/profile/my-work?${WorkStateQueryType.USERID}=${member.user_id}&${WorkStateQueryType.START_DATE}=${state.startDate}&${WorkStateQueryType.END_DATE}=${state.endDate}`
      )
    }

    onMounted(() => {
      getCompleteWorkData()
    })

    return {
      ...toRefs(state),
      nowDate,
      IconType,
      CheckboxType,
      CheckboxSizeType,
      changeDate,
      ChangeDateType,
      goMemberWork,
    }
  },
})
